// The one place a chart's visual values come from.
//
// Recharts ships strong defaults — light grids, 12px sans ticks, animated
// enters, generous margins — and a chart that accepts them looks like a
// Recharts chart rather than a Waypoint one. Every prop below resolves to a
// design token, so a chart is themed by composing these instead of by each
// widget re-deciding what a gridline looks like.
//
// CSS custom properties work here because Recharts puts these straight onto SVG
// attributes, and `stroke="var(--color-line)"` is valid SVG. That is what keeps
// charts on the same palette as the rest of the app for free.

/** Categorical series colours, validated (`node scripts/validate_palette.js`
 *  from the `dataviz` skill) against the dark surface across every adjacent
 *  pair — lightness band, chroma floor, CVD separation ≥ 12.2 deutan ΔE,
 *  normal-vision floor ≥ 20.3, contrast ≥ 3:1. Five, not three: an account
 *  can show more than three series at once (cash in several currencies plus
 *  its reconstructed invested line), and `i % length` would then wrap back to
 *  slot 0, painting two balances the same colour. Assignment is
 *  always by fixed index, never cycled or reassigned — the two new slots
 *  don't change what the first three mean to an existing chart.
 *
 *  Assign in fixed order, never cycled: a filter that removes a series must
 *  not repaint the survivors. */
export const SERIES_COLORS = ["#a278e4", "#0f9f92", "#c07f1a", "#3d8fe0", "#d65a7a"] as const;

/** The colours a chart draws besides the series': neutrals for what is
 *  folded or set beside the series, and two hues for parts no series colour
 *  carries. Hex, as a hatch's tint needs. */
export const CHART_COLORS = {
  neutral: "#6f6f78",
  grey: "#8a8a92",
  slate: "#4f4f5a",
  silver: "#9aa0a8",
  plum: "#9a3d8f",
  rose: "#e0556a",
} as const;

/** Semantic, and separate from the categorical set above — a status colour is
 *  never "series 4". */
export const SEMANTIC = {
  positive: "var(--color-positive)",
  negative: "var(--color-negative)",
  accent: "var(--color-accent)",
} as const;

const TICK = { fill: "var(--color-faint)", fontSize: 9.5, fontWeight: 700 } as const;

/** Horizontal rules only. Vertical gridlines on a time axis imply the months
 *  are sampled at those points, which they are not — the axis is categorical. */
export const gridProps = {
  stroke: "var(--color-line)",
  strokeDasharray: "0",
  vertical: false,
} as const;

export const xAxisProps = {
  tick: TICK,
  tickLine: false,
  axisLine: { stroke: "var(--color-line)" },
  minTickGap: 24,
} as const;

export const yAxisProps = {
  tick: TICK,
  tickLine: false,
  axisLine: false,
  width: 48,
} as const;

/** Animation is off app-wide. A number that slides into place is a number you
 *  cannot read yet, and this is a dense, information-first UI — the charts are
 *  read, not watched. */
export const seriesProps = { isAnimationActive: false } as const;

/** Recharts' own cursor is a filled block; a hairline is enough to say "this
 *  column". */
export const cursorProps = { stroke: "var(--color-line-strong)", strokeWidth: 1 } as const;

export const CHART_MARGIN = { top: 8, right: 8, bottom: 0, left: 0 } as const;
